import { useFieldArray, useFormContext } from "react-hook-form";
import { fileToBase64 } from "../../utils";
import { common_file_upload, serve_file } from "../../constants/apis";
import { useAppContext } from "../../context/AppContext";
import { toast } from "react-toastify";

import DocumentIcon from "../Icons/DocumentIcon";
import DragAndDrop from "../DragAndDrop";
import { FullRegistrationFormType } from "../../schemas/XFlowFullRegSchema";

import Modal from "../Modal";
import { useRef, useState } from "react";
import { SpinnerIcon } from "../Icons";
import { businessDocumentsOptions } from "../../constants/xFlow";
import Dropdown from "../DropDown";
import InputWithLabel from "../InputWithLabel";

export default function BusinessDocumentsFields() {
  const { jwtToken } = useAppContext();
  const viewDocumentDialogRef = useRef<HTMLDialogElement>(null);
  const [documentFileData, setDocumentFileData] = useState({
    fileUrl: "",
    mimeType: "",
    loading: false,
    error: "",
  });

  const toggleDialog = () => {
    if (viewDocumentDialogRef.current?.hasAttribute("open")) {
      viewDocumentDialogRef.current?.close();
      const imgEl = document.getElementById(`blob-data`) as HTMLImageElement;
      imgEl.src = "";
      setDocumentFileData((prev) => ({ ...prev, error: "" }));
    } else {
      viewDocumentDialogRef.current?.showModal();
    }
  };

  async function getBlobFileData(url: string, jwtToken: string) {
    setDocumentFileData((prev) => ({ ...prev, loading: true }));

    try {
      const res = await fetch(`${serve_file}?file=${url}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      const blob = await res.blob();
      // console.log(blob);

      const newUrl = URL.createObjectURL(blob);
      setDocumentFileData((prev) => ({
        ...prev,
        fileUrl: newUrl,
        mimeType: blob.type,
      }));
    } catch (error) {
      console.log(error);
      setDocumentFileData({
        fileUrl: "",
        mimeType: "",
        loading: false,
        error: "Something went wrong please try again!",
      });
    } finally {
      setDocumentFileData((prev) => ({ ...prev, loading: false }));
    }
  }

  const {
    control,
    formState: { errors },
    register,
    setValue,
    getValues,
    clearErrors,
    watch,
  } = useFormContext<FullRegistrationFormType>();

  const {
    fields: businessDocumentsFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "business_documents_details",
  });

  const uploadFile = async (file: File, index: number) => {
    try {
      const base64String = await fileToBase64(file);

      const res = await fetch(common_file_upload, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Content_Type: "application/json",
        },
        body: JSON.stringify({
          base64File: base64String,
          folder_name: "user",
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        // clearErrors(`business_documents_details.${index}.file`);

        setValue(
          `business_documents_details.${index}.document_file_path`,
          data.url as string,
        );

        clearErrors(`business_documents_details.${index}.document_file_path`);
        // toast.success(data.msg);
      } else {
        // toast.error("File upload went wrong please try again");
      }
    } catch (error) {
      console.error("Error converting file to Base64:", error);
      // toast.error("File upload went wrong please try again");
    }
  };

  const handleFileChangeAndUpload = async (
    e: React.FormEvent<HTMLInputElement>,
    index: number,
  ) => {
    const file = e.currentTarget.files?.[0];

    if (file) {
      await uploadFile(file, index);
    }
  };

  const handleDragAndUpload = async (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.preventDefault();

    const file = e.dataTransfer.files?.[0];
    console.log(file);

    if (file) {
      await uploadFile(file, index);
    }
  };

  return (
    <>
      <div>
        <hr />

        {businessDocumentsFields?.map((field, index) => (
          <div key={field.id} className="text-[#344054]">
            <h3 className={` font-medium py-4 ${index !== 0 && "border-t"}`}>
              Document
            </h3>
            <div className="flex flex-col gap-3 mb-2">
              <div className="flex gap-6 mb-2">
                <Dropdown
                  options={businessDocumentsOptions}
                  id="businessDocumentNames"
                  // placeholder="Please Select Document Type"
                  label={`Document Type`}
                  error={
                    errors.business_documents_details?.[index]?.document_type
                      ?.message
                  }
                  {...register(
                    `business_documents_details.${index}.document_type`,
                    {
                      value: "GSTIN",
                    },
                  )}
                />

                <InputWithLabel
                  label={watch(
                    `business_documents_details.${index}.document_type`,
                  )}
                  placeholder={`Enter ${getValues(
                    `business_documents_details.${index}.document_type`,
                  )}`}
                  {...register(
                    `business_documents_details.${index}.document_number`,
                  )}
                  error={
                    errors.business_documents_details?.[index]?.document_number
                      ?.message
                  }
                />
              </div>

              <div className="relative">
                <hr />
                <p className="absolute -top-3 left-1/2 bg-white px-1">Or</p>
              </div>

              <div>
                <label className="text-sm font-medium">
                  Company Authorization{" "}
                  {watch(`business_documents_details.${index}.document_type`)}{" "}
                  Document
                </label>
                {getValues()?.business_documents_details?.[index]
                  ?.document_file_path ? (
                  <button
                    type="button"
                    className="text-sm flex float-end items-center gap-1 text-custom-blue hover:underline"
                    // rel="noopener noreferrer" // Important for security
                    onClick={() => {
                      toggleDialog();
                      getBlobFileData(
                        getValues()?.business_documents_details?.[index]
                          ?.document_file_path!,
                        jwtToken!,
                      );
                    }}
                  >
                    <DocumentIcon className="size-[14px]" /> View your uploaded
                    document
                  </button>
                ) : null}

                <DragAndDrop
                  id={`file-input-${index}`}
                  onChange={(e) => {
                    handleFileChangeAndUpload(e, index);

                    toast.promise(handleFileChangeAndUpload(e, index), {
                      pending: "File is uploading",
                      success: "File has been uploaded 👌",
                      error: "Rejected, please try again 🤯",
                    });
                  }}
                  onDrop={(e) => {
                    handleDragAndUpload(e, index);

                    toast.promise(handleDragAndUpload(e, index), {
                      pending: "File is uploading",
                      success: "File has been uploaded 👌",
                      error: "Rejected, please try again 🤯",
                    });
                  }}
                />

                {/* Display file validation/error messages */}
                <small className="text-red-500 h-[10px] block ml-1">
                  {
                    // errors.business_documents_details?.[index]?.file?.message ||
                    errors.business_documents_details?.[index]
                      ?.document_file_path?.message
                  }
                </small>
              </div>

              {index !== 0 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="w-10 text-start text-red-500 hover:underline"
                >
                  {/* <CrossIcon className="size-8 text-red-500" /> */}
                  remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* document dialog */}
      <Modal
        ref={viewDocumentDialogRef}
        toggleDialog={toggleDialog}
        className="max-w-[600px] w-full p-8 rounded-md backdrop:bg-black/50"
      >
        <h3 className="text-xl mb-4">Document</h3>
        <div className="flex justify-center">
          {documentFileData.fileUrl &&
            documentFileData.mimeType.startsWith("image/") && (
              <img
                id={`blob-data`}
                src={documentFileData.fileUrl}
                className="h-[400px]"
              />
            )}

          {documentFileData.loading && (
            <SpinnerIcon className="size-8 text-gray-200 animate-spin fill-blue-600" />
          )}
          {documentFileData.error && (
            <p className="text-red-400">{documentFileData.error}</p>
          )}
        </div>
        <div className="h-[500px] flex justify-center">
          {documentFileData.fileUrl &&
            documentFileData.mimeType === "application/pdf" && (
              <iframe id={`blob-data`} src={documentFileData.fileUrl} />
            )}
        </div>
      </Modal>

      {/* <div>
        <button
          type="button"
          className="h-[44px] px-2 rounded-md bg-custom-blue-medium text-white font-medium"
          onClick={() =>
            append({ documents_type: "", documents_file_path: "" })
          }
        >
          Add A New Document
        </button>
      </div> */}
    </>
  );
}
